import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  requestNotificationPermission,
  getNotificationPermission,
  isFCMSupported,
  IS_FCM_ENABLED
} from '../services/firebase';
import CoupangAd from '../components/CoupangAd';
import './ParentSettings.css';

const ParentSettings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [familySettings, setFamilySettings] = useState({
    weekly_allowance: 5000,
    settlement_mode: 'carryover',
    bonus_amount: 500,
    penalty_amount: 1000
  });

  // 알림 관련 상태
  const [notificationStatus, setNotificationStatus] = useState({
    permission: 'default',
    isSupported: false,
    token: null,
    tokens: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/parent/login');
      return;
    }

    loadUserData();
    loadFamilySettings();
    checkNotificationStatus();
  }, [navigate]);

  const loadUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v0/auth/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        navigate('/parent/login');
      }
    } catch (error) {
      console.error('사용자 정보 로딩 오류:', error);
      navigate('/parent/login');
    }
  };

  const loadFamilySettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v0/families/me/settings', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFamilySettings(data);
      }
    } catch (error) {
      console.error('가족 설정 로딩 오류:', error);
    }
  };

  const checkNotificationStatus = async () => {
    const permission = getNotificationPermission();
    const isSupported = isFCMSupported();

    setNotificationStatus(prev => ({
      ...prev,
      permission,
      isSupported
    }));

    // 알림 설정 조회
    if (isSupported) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/v0/auth/notify/settings', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setNotificationStatus(prev => ({
            ...prev,
            tokens: data.tokens || []
          }));
        }
      } catch (error) {
        console.error('알림 설정 조회 오류:', error);
      }
    }
  };

  const handleSettingsUpdate = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v0/families/me/settings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(familySettings)
      });

      if (response.ok) {
        setMessage('설정이 저장되었습니다.');
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('설정 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('설정 저장 오류:', error);
      setMessage('설정 저장 중 오류가 발생했습니다.');
    }
  };

  const handleNotificationRequest = async () => {
    if (!isFCMSupported()) {
      setMessage('이 브라우저는 알림을 지원하지 않습니다.');
      return;
    }

    setIsLoading(true);
    setMessage('알림 권한을 요청하고 있습니다...');

    try {
      console.log('알림 활성화 시작');

      const fcmToken = await requestNotificationPermission();
      console.log('FCM 토큰 획득 완료');

      setMessage('서버에 토큰을 저장하는 중...');

      // 서버에 토큰 저장
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v0/auth/notify/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          token: fcmToken,
          platform: 'web'
        })
      });

      if (response.ok) {
        console.log('서버에 토큰 저장 완료');
        setNotificationStatus(prev => ({
          ...prev,
          permission: 'granted',
          token: fcmToken
        }));
        setMessage('알림이 활성화되었습니다! 🔔');

        // 토큰 목록 새로고침
        setTimeout(() => {
          checkNotificationStatus();
        }, 500);

      } else {
        const errorData = await response.json();
        console.error('서버 응답 오류:', errorData);
        throw new Error(errorData.error || '토큰 저장 실패');
      }
    } catch (error) {
      console.error('알림 권한 요청 실패:', error);
      setMessage(`알림 설정 실패: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestNotification = async () => {
    if (notificationStatus.permission !== 'granted') {
      setMessage('먼저 알림 권한을 허용해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v0/auth/notify/test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.development) {
          setMessage('🚧 푸시 알림 기능은 현재 개발 중입니다. 실제 알림은 곧 지원될 예정입니다.');
        } else if (data.demo) {
          setMessage('📱 데모 모드: 테스트 알림 전송이 시뮬레이션되었습니다.');
        } else {
          setMessage('테스트 알림이 전송되었습니다! 📨');
        }
      } else {
        throw new Error('테스트 알림 전송 실패');
      }
    } catch (error) {
      console.error('테스트 알림 오류:', error);
      setMessage('테스트 알림 전송에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/parent/login');
  };

  const getPermissionStatusIcon = () => {
    switch (notificationStatus.permission) {
      case 'granted': return '✅';
      case 'denied': return '❌';
      default: return '⚪';
    }
  };

  const getPermissionStatusText = () => {
    switch (notificationStatus.permission) {
      case 'granted': return '허용됨';
      case 'denied': return '차단됨';
      default: return '미설정';
    }
  };

  if (!user) {
    return <div className="loading">로딩 중...</div>;
  }

  return (
    <div className="parent-settings">
      <div className="settings-header">
        <button className="back-btn" onClick={() => navigate('/parent/dashboard')}>
          ← 뒤로가기
        </button>
        <h1>부모 설정</h1>
        <button className="btn-logout" onClick={handleLogout}>
          로그아웃
        </button>
      </div>

      <div className="settings-container">
        {/* 사용자 정보 */}
        <div className="settings-section">
          <h2>👤 계정 정보</h2>
          <div className="info-item">
            <label>이름:</label>
            <span>{user.name}</span>
          </div>
          <div className="info-item">
            <label>이메일:</label>
            <span>{user.email}</span>
          </div>
          <div className="info-item">
            <label>가족명:</label>
            <span>{user.familyName}</span>
          </div>
        </div>

        {/* 쿠팡 파트너스 다이나믹 배너 (iframe 버전) */}
        <CoupangAd type="iframe" category="family" />

        {/* 정산 설정 */}
        <div className="settings-section">
          <h2>💰 정산 설정</h2>
          <form onSubmit={handleSettingsUpdate}>
            <div className="form-group">
              <label htmlFor="weekly_allowance">주간 기본 용돈</label>
              <input
                type="number"
                id="weekly_allowance"
                value={familySettings.weekly_allowance}
                onChange={(e) => setFamilySettings(prev => ({
                  ...prev,
                  weekly_allowance: parseInt(e.target.value) || 0
                }))}
                min="0"
                step="100"
              />
              <span className="unit">원</span>
            </div>

            <div className="form-group">
              <label htmlFor="settlement_mode">정산 모드</label>
              <select
                id="settlement_mode"
                value={familySettings.settlement_mode}
                onChange={(e) => setFamilySettings(prev => ({
                  ...prev,
                  settlement_mode: e.target.value
                }))}
              >
                <option value="carryover">일반 (완료율 × 기본용돈)</option>
                <option value="cap">캠 모드 (필수 실패시 최대 50%)</option>
              </select>
              <div className="form-help">
                {familySettings.settlement_mode === 'carryover' ? (
                  <span>💡 완료율에 비례해서 용돈을 지급합니다. (80% 완료 = 80% 지급)</span>
                ) : (
                  <span>⚠️ 필수 미션을 하나라도 실패하면 최대 50%만 지급됩니다.</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="bonus_amount">보너스 금액</label>
              <input
                type="number"
                id="bonus_amount"
                value={familySettings.bonus_amount}
                onChange={(e) => setFamilySettings(prev => ({
                  ...prev,
                  bonus_amount: parseInt(e.target.value) || 0
                }))}
                min="0"
                step="100"
              />
              <span className="unit">원</span>
            </div>

            <div className="form-group">
              <label htmlFor="penalty_amount">벌점 금액</label>
              <input
                type="number"
                id="penalty_amount"
                value={familySettings.penalty_amount}
                onChange={(e) => setFamilySettings(prev => ({
                  ...prev,
                  penalty_amount: parseInt(e.target.value) || 0
                }))}
                min="0"
                step="100"
              />
              <span className="unit">원</span>
            </div>

            <button type="submit" className="btn-primary">
              설정 저장
            </button>
            {message && (
              <div className={`message ${message.includes('오류') || message.includes('실패') ? 'error' : 'success'}`}>
                {message}
              </div>
            )}
          </form>
        </div>

        {/* 알림 설정 */}
        <div className="settings-section">
          <h2>🔔 알림 설정</h2>

          <div className="development-notice">
            <span className="dev-badge">개발 중</span>
            푸시 알림 기능은 현재 개발 중입니다. 곧 지원될 예정입니다.
          </div>

          <div className="notification-status">
            <div className="status-item">
              <span className="status-icon">{getPermissionStatusIcon()}</span>
              <div className="status-info">
                <strong>알림 권한:</strong> {getPermissionStatusText()}
                {!notificationStatus.isSupported && (
                  <div className="status-note">
                    ⚠️ 이 브라우저는 웹 푸시 알림을 지원하지 않습니다.
                  </div>
                )}
                {notificationStatus.isSupported && window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && (
                  <div className="status-note">
                    ⚠️ HTTPS 환경에서만 알림이 작동합니다.
                  </div>
                )}
              </div>
            </div>

            {notificationStatus.tokens.length > 0 && (
              <div className="tokens-info">
                <strong>등록된 기기:</strong>
                {notificationStatus.tokens.map((tokenInfo, index) => (
                  <div key={index} className="token-item">
                    📱 {tokenInfo.platform === 'web' ? '웹' : '안드로이드'}
                    {tokenInfo.is_active ? ' (활성)' : ' (비활성)'}
                    <small> - {new Date(tokenInfo.updated_at).toLocaleDateString()}</small>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="notification-actions">
            <button
              className="btn-notification btn-disabled"
              disabled={true}
            >
              🚧 개발 중 - 사용 불가
            </button>
          </div>

          <div className="notification-info">
            <h4>📋 알림 받을 수 있는 상황:</h4>
            <ul>
              <li>✅ 자녀가 미션을 체크했을 때</li>
              <li>📊 주간 정산 미리보기 알림</li>
              <li>🎯 미션 완료율 알림</li>
              <li>⚠️ 중요한 시스템 알림</li>
            </ul>

            <div className="platform-support">
              <strong>지원 플랫폼:</strong>
              <span className="support-item">🌐 웹 브라우저 ✅</span>
              <span className="support-item">📱 안드로이드 ✅</span>
              <span className="support-item">🍎 iOS ❌</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ParentSettings;